import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Alert, 
  CircularProgress 
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const AdminSetup = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { user } = useAuth();

  const handleMakeAdmin = async () => {
    if (!user) {
      setError('You must be logged in to use this feature');
      return;
    }

    setLoading(true);
    setMessage('');
    setError('');

    try {
      // First try to call the initializeUser function to create the user document
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../config/firebase');
      
      try {
        const initializeUser = httpsCallable(functions, 'initializeUser');
        await initializeUser();
        console.log('User document initialized');
      } catch (initError) {
        console.log('User document might already exist or initializeUser failed:', initError);
      }

      // Now try to make the user admin
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin-setup/make-admin/${user.uid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await user.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage('✅ You are now an admin! Please refresh the page to see admin features.');
      } else {
        setError(data.error || 'Failed to update role');
      }
    } catch (err) {
      console.error('Error making user admin:', err);
      setError('Failed to update role. Please try the manual method below.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckRole = async () => {
    if (!user) {
      setError('You must be logged in to use this feature');
      return;
    }

    setLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin-setup/check-role/${user.uid}`, {
        headers: {
          'Authorization': `Bearer ${await user.getIdToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`Current role: ${data.role} (Email: ${data.email})`);
      } else {
        setError(data.error || 'Failed to check role');
      }
    } catch (err) {
      console.error('Error checking role:', err);
      setError('Failed to check role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please log in to access admin setup.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          Admin Setup (Development Only)
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          This is a development tool to test admin functionality. In production, 
          admin roles should be assigned securely by existing administrators.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Current User:</strong> {user.email}
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleCheckRole}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Check Current Role'}
            </Button>
            
            <Button 
              variant="contained" 
              onClick={handleMakeAdmin}
              disabled={loading}
              color="warning"
            >
              {loading ? <CircularProgress size={20} /> : 'Create User & Make Admin'}
            </Button>
          </Box>
        </Box>

        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Alternative Method:
          </Typography>
          <Typography variant="body2">
            1. Open Firebase Emulator UI: <a href="http://127.0.0.1:4000" target="_blank" rel="noopener noreferrer">http://127.0.0.1:4000</a><br/>
            2. Go to Firestore Database<br/>
            3. Find the 'users' collection<br/>
            4. Find your user document<br/>
            5. Change the 'role' field from 'customer' to 'admin'<br/>
            6. Refresh this page
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminSetup;
